import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { CategoryBreakdown } from '../budget/CategoryBreakdown';
import { Meter, Panel } from '../common/Panel';

type Overview = RouterOutputs['insights']['overview'];

const TOP_COUNT = 6;

/**
 * The categories that took the most this period (everyday spending, not bills
 * or loans), with a way to open every category and the purchases behind it.
 */
export function TopCategoriesCard({
  overview,
  year,
  month,
}: {
  overview: Overview | undefined;
  year: number;
  month: number;
}) {
  const [showAll, setShowAll] = useState(false);
  const categories = overview?.categories ?? [];
  const uncategorized = overview?.uncategorized ?? { total: 0, count: 0 };
  const top = [...categories].sort((a, b) => b.total - a.total).slice(0, TOP_COUNT);
  const max = top[0]?.total ?? 0;
  const hasAny = categories.length > 0 || uncategorized.count > 0;

  return (
    <Panel title="Största kategorier" description="Rörliga köp, utan räkningar och lån">
      {top.length > 0 ? (
        <ul className="space-y-3">
          {top.map((c) => (
            <li key={c.id}>
              <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">
                  {c.name}
                  {c.groupName && <span className="text-muted-foreground"> · {c.groupName}</span>}
                </span>
                <span className="shrink-0 font-medium tabular-nums">{kr(c.total)}</span>
              </div>
              <Meter value={max > 0 ? c.total / max : 0} label={`${c.name}: ${kr(c.total)}`} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {uncategorized.count > 0
            ? 'Inga kategoriserade köp under perioden än.'
            : 'Inga köp under perioden än.'}
        </p>
      )}

      {hasAny && (
        <div className="mt-4 border-t border-border pt-1 sm:pt-3">
          <button
            type="button"
            aria-expanded={showAll}
            onClick={() => setShowAll((v) => !v)}
            className="flex min-h-11 w-full items-center justify-between gap-2 text-sm font-medium text-primary sm:min-h-0"
          >
            {showAll ? 'Dölj kategorierna' : 'Alla kategorier och köpen bakom'}
            <ChevronDown className={cn('size-4 transition-transform', showAll && 'rotate-180')} />
          </button>
          {showAll && (
            <div className="mt-4">
              <CategoryBreakdown
                year={year}
                month={month}
                categories={categories}
                uncategorized={uncategorized}
              />
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
